// File: js/db/sync.js
//
// The sync engine (spec §7). PUSH then PULL, on the same triggers: start · online ·
// after a write · timer. Every dependency that touches the world is INJECTED —
// transport (network), clock (timers), backoff (retry schedule) — so the whole
// engine runs deterministically in tests with no real timers and no network.
//
// ── PUSH ────────────────────────────────────────────────────────────────────
// Serialized drain in `queued_at` order. Acks are matched BY ID, never by array
// position: `sync_push` applies each event independently and its own comment says
// "Acks are returned IN ORDER but the client must match them BY ID" — a
// position-matched ack is a verdict applied to the wrong sale, which is how a
// conflict lands on a good row and a good row is silently dropped.
//
// A verdict is exactly one of three things (invariant 6 — nothing else exists):
//   inserted / already_present → mirror `synced:1`, delete the outbox row.
//                                `already_present` is SUCCESS: it is the kill-
//                                mid-sync case (harness F3) healing itself.
//   conflict                   → mirror the parked event into `conflicts`, delete
//                                the outbox row, raise the flag — and KEEP the
//                                ledger mirror row so the collected cash stays
//                                visible on screen. The pre-rewrite code wrote
//                                `sync_status = 'conflict'` and never read it
//                                anywhere (audit C1); this one is read by a human.
//   transport error            → keep the outbox row, exponential backoff, retry
//                                forever. A network error is a queue, not an error
//                                (audit M1: today it retries with no counter, no
//                                backoff and reports « Sync complete » each cycle).
//
// The first failure stops the WHOLE drain, so a later batch can never overtake an
// earlier one. And a thrown LOCAL-store error during ack application (quota,
// closed connection) schedules a retry exactly like a transport error — unguarded,
// that rejection kills the timer and retry chains until the app restarts.
//
// ── PULL ────────────────────────────────────────────────────────────────────
// Required, not optional. A push-only device never learns of a void, another
// desk's sale, or a restock, and its stock fold and daily totals diverge silently
// forever (harness F16).
//
// The cursor carries a 10-MINUTE OVERLAP WINDOW. Postgres `now()` is
// transaction-START time, so a long push commits rows stamped BEHIND a cursor that
// a concurrent pull has already advanced past; `>=` alone skips them permanently.
// Re-reading a window is free because the merge is idempotent (upsert by id) and
// every fold dedupes by id.
//
// A pulled event NEVER overwrites an outbox-pending row: the push ack owns that
// id's transition, and clobbering it with `synced:1` would delete money from the
// queue that the server has not actually got.

import { getMeta, setMeta, META_KEYS } from './schema.js';
import { requestPersist } from './persistence.js';
import { maybeRequestBlock } from './receipt-blocks.js';

/**
 * The pull overlap window (spec §7). Ten minutes is the documented figure: it must
 * comfortably exceed the longest plausible push transaction, because anything
 * shorter re-opens the permanently-skipped-row hole it exists to close.
 */
export const PULL_OVERLAP_MS = 10 * 60 * 1000;

/** How many events ride in one `sync_push` call. */
export const DEFAULT_BATCH_SIZE = 100;

/** Default wall clock and timers, wrapping the host. Injectable for tests. */
const defaultClock = {
    now: () => Date.now(),
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (h) => clearTimeout(h)
};

/**
 * Exponential backoff capped at 30 s, `attempt` 1-based: 1s, 2s, 4s … 30s, 30s …
 * Spec §7: `min(30s, 1000 · 2^(n−1))`.
 * @param {number} attempt
 * @returns {number} milliseconds
 */
export function defaultBackoff(attempt) {
    return Math.min(30000, 1000 * 2 ** (attempt - 1));
}

/**
 * Shift a cursor back by the overlap window (spec §7). `null` in, `null` out — a
 * device with no cursor pulls full history, which is what a first run must do.
 * @param {string|null} cursor ISO instant.
 * @returns {string|null}
 */
export function overlappedSince(cursor) {
    if (cursor == null) return null;
    const n = Date.parse(cursor);
    if (Number.isNaN(n)) return null;
    return new Date(n - PULL_OVERLAP_MS).toISOString();
}

/**
 * Create the sync engine bound to a Dexie db, a transport, and a device identity.
 *
 * @param {{
 *   db: import('dexie').Dexie,
 *   transport: {
 *     syncPush: (events:object[]) => Promise<{id:string, status:string, reason?:string}[]>,
 *     pullEvents: (q:{since:string|null}) => Promise<object[]>,
 *     pullConflicts: (q:{deviceId:string|null}) => Promise<object[]>,
 *     allocateReceiptBlock: (q:{deviceId:string, reason:string, size?:number}) => Promise<{block_start:number, block_end:number}>
 *   },
 *   deviceId?: string|null,
 *   clock?: {now:()=>number, setTimeout:Function, clearTimeout:Function},
 *   backoff?: (attempt:number) => number,
 *   timerIntervalMs?: number,
 *   batchSize?: number,
 *   navigator?: object
 * }} config
 * @returns {object} The engine handle.
 */
export function createSyncEngine(config) {
    const {
        db,
        transport,
        deviceId = null,
        clock = defaultClock,
        backoff = defaultBackoff,
        timerIntervalMs = 30000,
        batchSize = DEFAULT_BATCH_SIZE,
        navigator: nav
    } = config;

    let state = 'idle';       // 'idle' | 'syncing' | 'retrying' | 'stopped'
    let inFlight = null;      // the running sync() promise, or null
    let pendingSync = false;  // a trigger fired while a sync was in flight
    let attempt = 0;          // consecutive failures — drives the backoff
    let retryHandle = null;
    let timerHandle = null;
    let started = false;
    let onlineHandler = null;

    // Cached counts, so a synchronous guard (beforeunload) can answer without
    // touching async IndexedDB. Refreshed at the end of every round.
    let outboxCount = 0;
    let conflictCount = 0;
    let lastPullAt = null;

    // ---- status ------------------------------------------------------------

    async function refreshStatus() {
        outboxCount = await db.table('outbox').count();
        // OPEN rows only. The pull re-imports `reinjected` and `rejected` conflicts
        // every cycle; counting them would keep « {n} enregistrement(s) nécessitent
        // votre attention » lit forever after the admin resolved them, and a banner
        // that never clears is a banner nobody reads.
        conflictCount = await db.table('conflicts').filter(c => c && c.status === 'open').count();
        lastPullAt = await getMeta(db, META_KEYS.LAST_PULL_AT, null);
    }

    /** @returns {{state:string, outboxCount:number, conflictCount:number, lastPullAt:string|null}} */
    function status() {
        return { state, outboxCount, conflictCount, lastPullAt };
    }

    /** Synchronous — reads the cached count; safe inside a `beforeunload` handler. */
    function hasPendingWrites() {
        return outboxCount > 0;
    }

    // ---- push --------------------------------------------------------------

    /**
     * One push round.
     *
     * @returns {Promise<boolean>} `true` when the outbox drained (or was empty),
     *   `false` on a transport or local-store failure (retry scheduled, rows kept).
     */
    async function drain() {
        const rows = await db.table('outbox').orderBy('queued_at').toArray();
        if (rows.length === 0) {
            await refreshStatus();
            return true;
        }

        for (let i = 0; i < rows.length; i += batchSize) {
            const batch = rows.slice(i, i + batchSize);

            let results;
            try {
                results = await transport.syncPush(batch.map(r => r.payload));
            } catch {
                // Keep every row, back off, retry forever — and STOP. Continuing to
                // the next batch would let a later sale reach the server before an
                // earlier one, breaking the causal order the queue clock exists to
                // preserve.
                await failRound();
                return false;
            }

            try {
                await applyAcks(batch, results);
            } catch {
                // A LOCAL store error (quota exceeded, connection closing) during ack
                // application. The server kept the events; the next drain heals via
                // `already_present`. Treated exactly like a transport error — and
                // caught, because an unhandled rejection here kills the timer chain
                // until the app restarts.
                await failRound();
                return false;
            }
        }

        // A clean round resets the backoff and cancels any pending retry.
        attempt = 0;
        if (retryHandle != null) {
            clock.clearTimeout(retryHandle);
            retryHandle = null;
        }
        await refreshStatus();
        return true;
    }

    async function failRound() {
        scheduleRetry();
        state = 'retrying';
        await refreshStatus();
    }

    /**
     * Apply per-event verdicts in ONE transaction, matched BY ID.
     *
     * An id with no verdict is left queued: the next round re-pushes it and
     * `already_present` settles it. Silence is never read as success.
     *
     * @param {{id:string, payload:object}[]} rows
     * @param {{id:string, status:string, reason?:string}[]} results
     */
    async function applyAcks(rows, results) {
        const byId = new Map((results || []).filter(r => r && r.id != null).map(r => [r.id, r]));

        await db.transaction('rw', db.table('ledger'), db.table('outbox'), db.table('conflicts'), async () => {
            for (const row of rows) {
                const res = byId.get(row.id);
                if (!res) continue; // no verdict for this id → keep it queued

                if (res.status === 'inserted' || res.status === 'already_present') {
                    await db.table('ledger').update(row.id, { synced: 1 });
                    await db.table('outbox').delete(row.id);
                    continue;
                }

                // `conflict` — and anything else. Invariant 6 says exactly three
                // outcomes exist, so a fourth is a contract breach; parking it in
                // front of a human beats retrying it silently until the heat death
                // of the universe.
                const reason = res.status === 'conflict'
                    ? (res.reason ?? 'conflict')
                    : `unknown-ack-status:${res.status}`;

                await db.table('conflicts').put({
                    id: row.id,                 // the EVENT uuid — same key the pull path uses
                    ledger_event: row.payload,  // the full original event, UUID included
                    reason,
                    status: 'open',
                    source: 'push',
                    device_id: row.payload && row.payload.device_id ? row.payload.device_id : deviceId,
                    raised_at: new Date(clock.now()).toISOString()
                });
                await db.table('outbox').delete(row.id);
                // The ledger mirror row is deliberately NOT touched: the cash was
                // collected, and it stays visible until a human resolves the park.
            }
        });
    }

    function scheduleRetry() {
        if (retryHandle != null) return; // one retry in flight at a time
        attempt += 1;
        const delay = backoff(attempt);
        // `async` + `await` so a deterministic fake clock can drive the retry chain
        // and observe each subsequent backoff. The real `setTimeout` ignores the
        // returned promise.
        retryHandle = clock.setTimeout(async () => {
            retryHandle = null;
            await sync();
        }, delay);
    }

    // ---- pull --------------------------------------------------------------

    /**
     * Upsert pulled events by id with `synced:1`, skipping any id that still has an
     * outbox row. Returns the events actually merged.
     * @param {object[]} events
     * @returns {Promise<object[]>}
     */
    async function mergeEvents(events) {
        if (!events || events.length === 0) return [];
        let merged = [];
        await db.transaction('rw', db.table('ledger'), db.table('outbox'), async () => {
            const pending = new Set((await db.table('outbox').toArray()).map(r => r.id));
            merged = events.filter(ev => ev && ev.id != null && !pending.has(ev.id));
            if (merged.length > 0) {
                await db.table('ledger').bulkPut(merged.map(ev => ({ ...ev, synced: 1 })));
            }
        });
        return merged;
    }

    /**
     * Merge this device's parked conflicts from the server, converging on the EVENT
     * uuid so the push-ack row and the pulled row are one row, not two.
     * @param {object[]} conflicts
     */
    async function mergeConflicts(conflicts) {
        if (!conflicts || conflicts.length === 0) return;
        await db.table('conflicts').bulkPut(conflicts.map(c => {
            const eventId = c && c.ledger_event && c.ledger_event.id ? c.ledger_event.id : c.id;
            return {
                id: eventId,
                server_conflict_id: c.id,
                device_id: c.device_id ?? null,
                ledger_event: c.ledger_event,
                reason: c.reason,
                status: c.status,               // open | reinjected | rejected
                source: 'pull',
                raised_at: c.raised_at ?? new Date(clock.now()).toISOString()
            };
        }));
    }

    /**
     * The newest `server_received_at` across `events`, never below `since`.
     *
     * The cursor advances from the TRUE maximum, so the overlap window widens the
     * query without ever regressing the cursor.
     * @param {object[]} events
     * @param {string|null} since
     * @returns {string|null}
     */
    function advanceCursor(events, since) {
        let best = since;
        let bestN = since != null ? Date.parse(since) : -Infinity;
        for (const e of events) {
            const s = e && e.server_received_at;
            if (s == null) continue;
            const n = Date.parse(s);
            if (!Number.isNaN(n) && n > bestN) {
                bestN = n;
                best = s;
            }
        }
        return best;
    }

    /** One pull round: new events, then this device's parked conflicts. */
    async function pull() {
        const since = await getMeta(db, META_KEYS.LAST_PULL_AT, null);

        // `null` on a first run → full history. Thereafter the 10-minute window.
        const events = await transport.pullEvents({ since: overlappedSince(since) });
        await mergeEvents(events || []);

        await mergeConflicts(await transport.pullConflicts({ deviceId }));

        const next = advanceCursor(events || [], since);
        if (next != null && next !== since) {
            await setMeta(db, META_KEYS.LAST_PULL_AT, next);
        }
        await refreshStatus();
        return events || [];
    }

    // ---- online tasks (block top-up + pull) --------------------------------

    async function onlineTasks() {
        if (deviceId != null) {
            try {
                await maybeRequestBlock(db, transport, deviceId);
            } catch {
                // Offline or server busy: the device keeps refusing collection until a
                // block arrives, which is the correct answer, not a degraded one.
            }
        }
        try {
            await pull();
        } catch {
            // The pull is best-effort; the next tick retries. It must never block or
            // fail the push path — money out ranks money in.
        }
    }

    // ---- orchestration -----------------------------------------------------

    /** Run push→pull once, coalescing concurrent triggers and looping while more arrive. */
    function sync() {
        if (inFlight) {
            pendingSync = true;
            return inFlight;
        }
        inFlight = (async () => {
            try {
                do {
                    pendingSync = false;
                    state = 'syncing';
                    const drained = await drain();
                    if (drained) await onlineTasks();
                } while (pendingSync);
                if (state !== 'retrying' && state !== 'stopped') state = 'idle';
            } finally {
                inFlight = null;
                await refreshStatus();
            }
        })();
        return inFlight;
    }

    /**
     * Trigger a sync after a local write. The cached status counts refresh when the
     * round completes, not at call time — a caller that needs the unload guard
     * current immediately should await the returned promise.
     */
    function kick() {
        return sync();
    }

    // ---- lifecycle ---------------------------------------------------------

    function scheduleTimer() {
        timerHandle = clock.setTimeout(async () => {
            await sync();
            if (state !== 'stopped') scheduleTimer();
        }, timerIntervalMs);
    }

    async function start() {
        if (started) return;
        started = true;
        state = 'idle';
        await requestPersist(db, { navigator: nav });
        await refreshStatus();
        if (typeof addEventListener === 'function') {
            onlineHandler = () => void sync();
            addEventListener('online', onlineHandler);
        }
        scheduleTimer();
        await sync();
    }

    function stop() {
        state = 'stopped';
        started = false;
        if (retryHandle != null) clock.clearTimeout(retryHandle);
        if (timerHandle != null) clock.clearTimeout(timerHandle);
        retryHandle = null;
        timerHandle = null;
        if (onlineHandler && typeof removeEventListener === 'function') {
            removeEventListener('online', onlineHandler);
            onlineHandler = null;
        }
    }

    return { start, stop, sync, kick, drain, pull, status, hasPendingWrites, refreshStatus };
}

/**
 * The `beforeunload` handler that warns while this device holds unsynced cash.
 * Returned as a plain function so it can be unit-tested without a real browser.
 *
 * @param {{hasPendingWrites: () => boolean}} engine
 * @returns {(event: {preventDefault?: () => void, returnValue?: string}) => (string|undefined)}
 */
export function createUnloadGuard(engine) {
    return function beforeUnloadHandler(event) {
        if (engine.hasPendingWrites()) {
            if (event) {
                if (typeof event.preventDefault === 'function') event.preventDefault();
                event.returnValue = '';
            }
            return '';
        }
        return undefined;
    };
}
